import { Fragment, useContext, useEffect, useMemo, useState } from "react"
import moment from "moment"
import styles from "./payments.module.scss"

import { BookingContext } from "../../../utils/providers/booking"
import { AlertsContext } from "../../../utils/providers/alerts"
import { AuthContext } from "../../../utils/providers/auth"
import { db, funEU } from "../../../utils/firebase"

import Badge from "../../../components/ui/badge/badge"
import Button from "../../../components/ui/button/button"
import AddPayment from "../../../components/payments/add/add"
import SendLink from "../../../components/payments/send-link/send-link"
import Refund from "../../../components/payments/refund/refund"
import TakeCard from "../../../components/payments/take-card/take-card"
import GiftVouchers from "../../../components/payments/gift-vouchers/gift-vouchers"
import Window from "../../../components/structure/window/window"
import { PlusIcon, LinkIcon, CopyIcon } from "../../../utils/svgs"
import { ChevronDown, CreditCard, Gift } from "lucide-react"

const resendPaymentLinkCallable = funEU.httpsCallable("resendPaymentLink")
const cancelPaymentLinkCallable = funEU.httpsCallable("cancelPaymentLink")

/**
 * Resolve the displayed Source label by combining the record's `source`
 * with its `stripe_channel` (set by the MOTO / customer-portal flows) and
 * its `stripe_payment_link_id` (set when a Stripe Payment Link was paid).
 *
 * Legacy STRIPE records that pre-date the channel field fall back to just
 * "Card" — they came from the Payments tab before we tracked origin.
 */
const resolveSourceLabel = (row) => {
	const { source, raw = {} } = row
	const channel = raw.stripe_channel
	const linkId = raw.stripe_payment_link_id

	if (source === "STRIPE_LINK") return "Card - Payment link"
	if (source === "STRIPE") {
		if (channel === "MOTO") return "Card - Staff/MOTO"
		if (channel === "CUSTOMER_CARD") return "Card - Client/Portal"
		if (channel === "CUSTOMER_BANK") return "Bank transfer - Client/Portal"
		if (linkId) return "Card - Payment link"
		return "Card"
	}
	if (source === "MANUAL") return "Manual"
	if (source === "VOUCHER") return "Voucher"
	if (source === "STRIPE_REFUND") return "Refund"
	if (source === "WORLDPAY_LEGACY") return "WorldPay (legacy)"
	return source || "—"
}

const STATUS_LABELS = {
	SUCCEEDED: { label: "Succeeded", type: "POSITIVE" },
	REFUNDED: { label: "Refunded", type: "NEGATIVE" },
	PARTIALLY_REFUNDED: { label: "Partially refunded", type: "AMBER" },
	AWAITING: { label: "Awaiting payment", type: "AMBER" },
	CANCELLED: { label: "Cancelled", type: "" },
}

const formatCurrency = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

const tsToMoment = (ts) => {
	if (!ts) return null
	if (typeof ts.seconds === "number") return moment(ts.seconds, "X")
	if (ts._seconds) return moment(ts._seconds, "X")
	return null
}

/**
 * Booking Payments tab.
 *
 * Renders a unified history of completed payments + active/cancelled Stripe
 * Payment Links. Each row is expandable; the detail panel exposes
 * resend/cancel actions for pending links and fee breakdowns for paid
 * Stripe charges.
 */
export default function Payments({ client, clientID }) {
	const { bookingID, paidByClient, outstanding, balanceDue } = useContext(BookingContext)
	const { pushAlert } = useContext(AlertsContext)
	const { user } = useContext(AuthContext)

	const isAdmin = Boolean(user?.is_admin || user?.is_super_admin)

	const [payments, setPayments] = useState([])
	const [paymentLinks, setPaymentLinks] = useState([])
	const [loadingHistory, setLoadingHistory] = useState(true)
	const [loadingLinks, setLoadingLinks] = useState(true)
	const [addingPayment, setAddingPayment] = useState(false)
	const [sendingLink, setSendingLink] = useState(false)
	const [sendLinkPrefill, setSendLinkPrefill] = useState(null)
	const [takingCard, setTakingCard] = useState(false)
	const [applyingVoucher, setApplyingVoucher] = useState(false)
	const [refundingPayment, setRefundingPayment] = useState(null)
	const [expandedId, setExpandedId] = useState(null)
	const [busyLinkId, setBusyLinkId] = useState(null)
	const [busyAction, setBusyAction] = useState(null)
	const [userNames, setUserNames] = useState({})

	useEffect(() => {
		if (!bookingID) return

		const unsubscribePayments = db
			.collection(`bookings/${bookingID}/payments`)
			.orderBy("paid_date", "desc")
			.onSnapshot((paymentDocs) => {
				setPayments(paymentDocs.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
				setLoadingHistory(false)
			})

		const unsubscribeLinks = db.collection(`bookings/${bookingID}/payment_links`).onSnapshot((linkDocs) => {
			setPaymentLinks(linkDocs.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
			setLoadingLinks(false)
		})

		return () => {
			unsubscribePayments()
			unsubscribeLinks()
		}
	}, [bookingID])

	// Lazily resolve user-id → display-name for any `saved_by` / `cancelled_by`
	// references in the loaded data. payment_links already carry `sent_by_name`
	// so we only need to fetch for uids we don't already have.
	useEffect(() => {
		const uids = new Set()
		payments.forEach((p) => p.saved_by && uids.add(p.saved_by))
		paymentLinks.forEach((l) => l.cancelled_by && uids.add(l.cancelled_by))

		const missing = Array.from(uids).filter((uid) => !(uid in userNames))
		if (missing.length === 0) return

		let cancelled = false

		Promise.all(
			missing.map((uid) =>
				db
					.doc(`users/${uid}`)
					.get()
					.then((snap) => {
						const data = snap.data() || {}
						const name = [data.first_name, data.last_name].filter(Boolean).join(" ") || uid
						return [uid, name]
					})
					.catch(() => [uid, uid]),
			),
		).then((pairs) => {
			if (cancelled) return
			setUserNames((current) => {
				const next = { ...current }
				pairs.forEach(([uid, name]) => {
					next[uid] = name
				})
				return next
			})
		})

		return () => {
			cancelled = true
		}
	}, [payments, paymentLinks, userNames])

	const rows = useMemo(() => {
		const merged = []

		payments.forEach((p) => {
			merged.push({
				id: `payment-${p.id}`,
				kind: "PAYMENT",
				rawId: p.id,
				date: tsToMoment(p.paid_date),
				payee: p.payee || "—",
				reference: p.reference || "—",
				source: p.source || "MANUAL",
				status: p.status || "SUCCEEDED",
				amount: Number(p.amount || 0),
				raw: p,
			})
		})

		paymentLinks.forEach((l) => {
			// Skip PAID links — their matched payment record represents the row.
			if (l.status === "PAID") return

			merged.push({
				id: `link-${l.id}`,
				kind: "LINK",
				rawId: l.id,
				date: tsToMoment(l.sent_at) || tsToMoment(l.last_sent_at),
				payee: l.payee || "—",
				reference: "Awaiting checkout",
				source: "STRIPE_LINK",
				status: l.status === "CANCELLED" ? "CANCELLED" : "AWAITING",
				amount: Number(l.amount || 0),
				raw: l,
			})
		})

		merged.sort((a, b) => {
			const aMs = a.date ? a.date.valueOf() : 0
			const bMs = b.date ? b.date.valueOf() : 0
			return bMs - aMs
		})

		return merged
	}, [payments, paymentLinks])

	const balanceDueLabel = balanceDue && balanceDue !== 0 ? moment(balanceDue, "x").format("DD/MM/YYYY") : "No date set"
	const loading = loadingHistory || loadingLinks

	const handleResend = async (row) => {
		setBusyLinkId(row.id)
		setBusyAction("resend")
		try {
			await resendPaymentLinkCallable({ bookingID, linkID: row.rawId })
			pushAlert({ type: "SUCCESS", title: "Payment link re-sent" })
		} catch (err) {
			pushAlert({ type: "ERROR", title: err?.message || "Could not resend." })
		} finally {
			setBusyLinkId(null)
			setBusyAction(null)
		}
	}

	const handleCancel = async (row) => {
		setBusyLinkId(row.id)
		setBusyAction("cancel")
		try {
			await cancelPaymentLinkCallable({ bookingID, linkID: row.rawId })
			pushAlert({ type: "SUCCESS", title: "Payment link cancelled" })
		} catch (err) {
			pushAlert({ type: "ERROR", title: err?.message || "Could not cancel." })
		} finally {
			setBusyLinkId(null)
			setBusyAction(null)
		}
	}

	const handleCopy = async (url) => {
		try {
			await navigator.clipboard.writeText(url)
			pushAlert({ type: "SUCCESS", title: "Link copied" })
		} catch {
			pushAlert({ type: "ERROR", title: "Couldn't copy — copy it manually." })
		}
	}

	return (
		<>
			<div className={styles.container}>
				<div className={styles.actionBar}>
					<Button
						label="Add payment"
						icon={<PlusIcon />}
						small={true}
						onClick={() => setAddingPayment(true)}
					/>
					<Button
						label="Take card (MOTO)"
						icon={<CreditCard size={14} strokeWidth={1.75} />}
						small={true}
						onClick={() => setTakingCard(true)}
					/>
					<Button
						label="Send payment link"
						icon={<LinkIcon />}
						small={true}
						onClick={() => {
							setSendLinkPrefill(null)
							setSendingLink(true)
						}}
					/>
					<Button
						label="Apply gift voucher"
						icon={<Gift size={14} strokeWidth={1.75} />}
						small={true}
						onClick={() => setApplyingVoucher(true)}
					/>
				</div>

				<div className={styles.summaryLine}>
					<span>
						Paid <strong>£{formatCurrency(paidByClient)}</strong>
					</span>
					<span>
						Outstanding <strong>£{formatCurrency(outstanding)}</strong>
					</span>
					<span>
						Balance due <strong>{balanceDueLabel}</strong>
					</span>
				</div>

				<p className={styles.historyTitle}>Payment history</p>

				{loading && <p className={styles.historyLoading}>Loading payments...</p>}

				{!loading && rows.length === 0 && <p className={styles.historyEmpty}>No payments recorded yet.</p>}

				{!loading && rows.length > 0 && (
					<table className={styles.historyTable}>
						<thead>
							<tr>
								<th>Date</th>
								<th>Payee</th>
								<th>Reference</th>
								<th>Source</th>
								<th>Status</th>
								<th style={{ textAlign: "right" }}>Amount</th>
								<th style={{ width: 22 }} />
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => {
								const statusMeta = STATUS_LABELS[row.status] || STATUS_LABELS.SUCCEEDED
								const isExpanded = expandedId === row.id
								const isNegative = row.amount < 0

								return (
									<Fragment key={row.id}>
										<tr
											className={[styles.row, isExpanded ? styles.rowExpanded : ""].join(" ")}
											onClick={() => setExpandedId(isExpanded ? null : row.id)}>
											<td>{row.date ? row.date.format("DD/MM/YYYY") : "—"}</td>
											<td>{row.payee}</td>
											<td className={styles.reference}>{row.reference}</td>
											<td className={styles.reference}>{resolveSourceLabel(row)}</td>
											<td>
												<Badge
													type={statusMeta.type}
													label={statusMeta.label}
												/>
											</td>
											<td className={[styles.amount, isNegative ? styles.amountNegative : ""].join(" ")}>£{formatCurrency(row.amount)}</td>
											<td className={styles.chevron}>
												<ChevronDown
													size={14}
													strokeWidth={1.75}
												/>
											</td>
										</tr>

										{isExpanded && (
											<tr className={styles.detailRow}>
												<td colSpan={7}>
													<RowDetail
														row={row}
														client={client}
														userNames={userNames}
														isAdmin={isAdmin}
														busy={busyLinkId === row.id}
														busyAction={busyLinkId === row.id ? busyAction : null}
														onResend={() => handleResend(row)}
														onCancel={() => handleCancel(row)}
														onCopy={() => handleCopy(row.raw.stripe_payment_link_url)}
														onRefund={() =>
															setRefundingPayment({
																paymentID: row.rawId,
																amount: row.amount,
																refundedAlready: Number(row.raw.refunded_amount || 0),
																reference: row.reference,
															})
														}
													/>
												</td>
											</tr>
										)}
									</Fragment>
								)
							})}
						</tbody>
					</table>
				)}
			</div>

			{addingPayment && (
				<AddPayment
					bookingID={bookingID}
					close={() => setAddingPayment(false)}
				/>
			)}

			{applyingVoucher && (
				<Window
					title="Apply a gift voucher"
					className="slim"
					close={() => setApplyingVoucher(false)}>
					<GiftVouchers
						bookingID={bookingID}
						clientID={clientID}
						onApplied={() => setApplyingVoucher(false)}
					/>
				</Window>
			)}

			{sendingLink && (
				<SendLink
					bookingID={bookingID}
					outstanding={outstanding}
					leadBooker={{
						email: client?.email,
						name: [client?.first_name, client?.last_name].filter(Boolean).join(" "),
					}}
					prefill={sendLinkPrefill}
					close={() => {
						setSendingLink(false)
						setSendLinkPrefill(null)
					}}
				/>
			)}

			{takingCard && (
				<TakeCard
					bookingID={bookingID}
					outstanding={outstanding}
					leadBooker={{
						email: client?.email,
						name: [client?.first_name, client?.last_name].filter(Boolean).join(" "),
					}}
					close={() => setTakingCard(false)}
					onFallbackToLink={(prefill) => {
						setSendLinkPrefill(prefill)
						setSendingLink(true)
					}}
				/>
			)}

			{refundingPayment && (
				<Refund
					bookingID={bookingID}
					paymentID={refundingPayment.paymentID}
					originalAmount={refundingPayment.amount}
					refundedAlready={refundingPayment.refundedAlready}
					originalReference={refundingPayment.reference}
					leadBooker={{
						email: client?.email,
						name: [client?.first_name, client?.last_name].filter(Boolean).join(" "),
					}}
					close={() => setRefundingPayment(null)}
				/>
			)}
		</>
	)
}

/**
 * Per-row detail panel. Shape varies by kind:
 *   - LINK + AWAITING: recipient, sender, sent-at, link URL (copy), resend, cancel
 *   - LINK + CANCELLED: same metadata + cancellation info
 *   - PAYMENT + STRIPE: recipient, fees, net, IDs
 *   - PAYMENT + other: minimal metadata
 */
function RowDetail({ row, userNames = {}, isAdmin = false, busy, busyAction, onResend, onCancel, onCopy, onRefund }) {
	if (row.kind === "LINK") {
		const link = row.raw
		const sentAt = tsToMoment(link.sent_at)
		const lastSent = tsToMoment(link.last_sent_at)
		const cancelledAt = tsToMoment(link.cancelled_at)

		return (
			<div className={styles.detailPanel}>
				<div className={styles.detailGrid}>
					<DetailField
						label="Recipient"
						value={link.recipient_email}
					/>
					<DetailField
						label="Sent by"
						value={link.sent_by_name}
					/>
					<DetailField
						label="Sent at"
						value={sentAt ? sentAt.format("DD MMM YYYY [at] HH:mm") : "—"}
					/>
					<DetailField
						label="Send count"
						value={`${link.send_count || 1}${lastSent && sentAt && lastSent.valueOf() !== sentAt.valueOf() ? ` (last ${lastSent.fromNow()})` : ""}`}
					/>
					{link.note && (
						<DetailField
							label="Note"
							value={link.note}
							wide={true}
						/>
					)}
					{link.cc_email && (
						<DetailField
							label="CC"
							value={link.cc_name ? `${link.cc_name} <${link.cc_email}>` : link.cc_email}
						/>
					)}
					<DetailField
						label="Stripe link"
						value={
							<span className={styles.urlInline}>
								<a
									href={link.stripe_payment_link_url}
									target="_blank"
									rel="noopener noreferrer">
									{link.stripe_payment_link_url}
								</a>
							</span>
						}
						wide={true}
					/>
					{cancelledAt && (
						<DetailField
							label="Cancelled at"
							value={cancelledAt.format("DD MMM YYYY [at] HH:mm")}
						/>
					)}
				</div>

				{row.status === "AWAITING" && (
					<div className={styles.detailActions}>
						<Button
							label="Copy link"
							icon={<CopyIcon />}
							type="secondary"
							small={true}
							onClick={onCopy}
							disabled={busy}
						/>
						<Button
							label="Resend email"
							type="secondary"
							small={true}
							loading={busyAction === "resend"}
							loadingText="Sending..."
							disabled={busy && busyAction !== "resend"}
							onClick={onResend}
						/>
						<Button
							label="Cancel link"
							small={true}
							loading={busyAction === "cancel"}
							loadingText="Cancelling..."
							disabled={busy && busyAction !== "cancel"}
							onClick={onCancel}
						/>
					</div>
				)}
			</div>
		)
	}

	// PAYMENT rows
	const payment = row.raw
	const isStripe = row.source === "STRIPE"
	const isRefund = row.source === "STRIPE_REFUND"

	const refundedAlready = Number(payment.refunded_amount || 0)
	const originalAmount = Number(payment.amount || 0)
	const remainingRefundable = Math.max(originalAmount - refundedAlready, 0)
	const canRefund = isAdmin && isStripe && remainingRefundable > 0.001 && payment.stripe_charge_id

	return (
		<div className={styles.detailPanel}>
			<div className={styles.detailGrid}>
				{isRefund && payment.refund_reason && (
					<DetailField
						label="Reason"
						value={payment.refund_reason}
						wide={true}
					/>
				)}
				{isRefund && payment.refunded_by_name && (
					<DetailField
						label="Refunded by"
						value={payment.refunded_by_name}
					/>
				)}
				{isRefund && tsToMoment(payment.created) && (
					<DetailField
						label="Refunded at"
						value={tsToMoment(payment.created).format("DD/MM/YYYY HH:mm")}
					/>
				)}
				{payment.receipt_sent_to && (
					<DetailField
						label={isRefund ? "Confirmation sent to" : "Receipt sent to"}
						value={payment.receipt_sent_to}
					/>
				)}
				{isStripe && refundedAlready > 0 && (
					<DetailField
						label="Refunded so far"
						value={`£${formatCurrency(refundedAlready)} of £${formatCurrency(originalAmount)}`}
					/>
				)}
				{(isStripe || isRefund) && typeof payment.stripe_fee_amount === "number" && (
					<DetailField
						label="Stripe fees"
						value={`£${formatCurrency(payment.stripe_fee_amount)}`}
					/>
				)}
				{(isStripe || isRefund) && typeof payment.stripe_net_amount === "number" && (
					<DetailField
						label="Net to GHD"
						value={`£${formatCurrency(payment.stripe_net_amount)}`}
					/>
				)}
				{(isStripe || isRefund) && payment.stripe_charge_id && (
					<DetailField
						label="Charge ID"
						value={payment.stripe_charge_id}
						wide={true}
					/>
				)}
				{isStripe && payment.stripe_payment_intent_id && (
					<DetailField
						label="Payment intent"
						value={payment.stripe_payment_intent_id}
						wide={true}
					/>
				)}
				{isRefund && payment.stripe_refund_id && (
					<DetailField
						label="Refund ID"
						value={payment.stripe_refund_id}
						wide={true}
					/>
				)}
				{!isStripe && !isRefund && tsToMoment(payment.created) && (
					<DetailField
						label="Saved at"
						value={tsToMoment(payment.created).format("DD/MM/YYYY HH:mm")}
					/>
				)}
				{!isStripe && !isRefund && payment.saved_by && (
					<DetailField
						label="Saved by"
						value={userNames[payment.saved_by] || payment.saved_by}
					/>
				)}
			</div>

			{canRefund && (
				<div className={styles.detailActions}>
					<Button
						label={refundedAlready > 0 ? "Refund the rest" : "Refund payment"}
						small={true}
						onClick={onRefund}
					/>
				</div>
			)}
		</div>
	)
}

function DetailField({ label, value, wide }) {
	return (
		<div className={[styles.detailField, wide ? styles.detailFieldWide : ""].join(" ")}>
			<p className={styles.detailLabel}>{label}</p>
			<p className={styles.detailValue}>{value}</p>
		</div>
	)
}
